/* =============================================================================
  File: assets/section-sticky-video.js
  - DOES NOT move iframe (keeps sound + timestamp)
  - Modal is CSS-only visual state
  - Minimize collapses when not in modal; closes modal when in modal
============================================================================= */
class SStickyVideo extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId || "ssv";
    this.modal = this.querySelector("[data-ssv-modal]");
    this.openEls = this.querySelectorAll("[data-ssv-open]");
    this.closeEls = this.querySelectorAll("[data-ssv-close]");
    this.muteBtns = this.querySelectorAll("[data-ssv-mute]");
    this.minimizeBtn = this.querySelector("[data-ssv-minimize]");
    this.restoreBtn = this.querySelector("[data-ssv-restore]");
    this.iconHosts = this.querySelectorAll("[data-ssv-icon]");
    this.iframe = this.querySelector("[data-ssv-iframe]");

    this.state = {
      muted: this.dataset.initialMuted === "true",
      collapsed: this.readCollapsed(),
      modalOpen: false
    };

    // --- FORCE OPEN on initial load when autoplay is enabled ---
    // If autoplay is enabled, the sticky should be visible (not collapsed) initially.
    // We also clear stored collapsed state so it doesn't "fight" autoplay.
    const autoplayEnabled =
      this.dataset.autoplay === "true" ||
      this.dataset.autoplay === "1" ||
      this.dataset.autoplay === "yes";

    if (autoplayEnabled && this.state.collapsed) {
      this.state.collapsed = false;
      this.writeCollapsed(false);
    }
    // ----------------------------------------------------------

    this.onKeyDown = this.onKeyDown.bind(this);
    this.closeModal = this.closeModal.bind(this);

    this.openEls.forEach((el) => {
      el.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!this.state.collapsed) this.openModal();
      });

      el.addEventListener("keydown", (e) => {
        if ((e.key === "Enter" || e.key === " ") && !this.state.collapsed) {
          e.preventDefault();
          this.openModal();
        }
      });
    });

    this.closeEls.forEach((el) => el.addEventListener("click", this.closeModal));

    this.muteBtns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleMute();
      });
    });

    this.minimizeBtn?.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (this.state.modalOpen) this.closeModal();
      else this.collapse();
    });

    this.restoreBtn?.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.expand();
    });

    // Click outside closes modal (backdrop)
    this.modal?.addEventListener("click", (e) => {
      if (e.target && e.target.hasAttribute("data-ssv-close")) this.closeModal();
    });

    this.applyCollapsedUI(this.state.collapsed);
    this.renderIcons();
    this.reapplyPlayerState();
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.onKeyDown);
  }

  storageKey() {
    return `ssv-collapsed:${this.sectionId}`;
  }

  readCollapsed() {
    try {
      return localStorage.getItem(this.storageKey()) === "1";
    } catch (_) {
      return false;
    }
  }

  writeCollapsed(val) {
    try {
      localStorage.setItem(this.storageKey(), val ? "1" : "0");
    } catch (_) {}
  }

  applyCollapsedUI(isCollapsed) {
    if (isCollapsed) this.classList.add("is-collapsed");
    else this.classList.remove("is-collapsed");
  }

  openModal() {
    if (!this.modal) return;

    this.state.modalOpen = true;
    this.classList.add("is-modal-open");
    this.modal.hidden = false;

    document.addEventListener("keydown", this.onKeyDown);

    // Keep playing, keep timestamp, just ensure audio state is correct
    this.ytCommand("playVideo");
    this.reapplyPlayerState();
  }

  closeModal() {
    if (!this.modal) return;

    this.state.modalOpen = false;
    this.classList.remove("is-modal-open");
    this.modal.hidden = true;

    document.removeEventListener("keydown", this.onKeyDown);

    // Keep playing in sticky (same iframe => same timestamp)
    this.ytCommand("playVideo");
    this.reapplyPlayerState();
  }

  onKeyDown(e) {
    if (e.key === "Escape") this.closeModal();
  }

  collapse() {
    if (this.state.modalOpen) this.closeModal();

    this.state.collapsed = true;
    this.writeCollapsed(true);
    this.applyCollapsedUI(true);

    // Pause while collapsed so it doesn't run hidden
    this.ytCommand("pauseVideo");
  }

  expand() {
    this.state.collapsed = false;
    this.writeCollapsed(false);
    this.applyCollapsedUI(false);

    // Continue where it paused (same iframe)
    this.ytCommand("playVideo");
    this.reapplyPlayerState();
  }

  toggleMute() {
    this.state.muted = !this.state.muted;
    this.applyAudioState();
    this.renderIcons();
  }

  reapplyPlayerState() {
    // Even without DOM moves, Safari/iOS can be flaky after fullscreen.
    const delays = [0, 120, 350, 800];
    delays.forEach((ms) => {
      window.setTimeout(() => this.applyAudioState(), ms);
    });
  }

  applyAudioState() {
    if (this.state.muted) {
      this.ytCommand("mute");
    } else {
      this.ytCommand("setVolume", [100]);
      this.ytCommand("unMute");
      this.ytCommand("setVolume", [100]);
    }
  }

  ytCommand(func, args = []) {
    if (!this.iframe?.contentWindow) return;
    this.iframe.contentWindow.postMessage(
      JSON.stringify({ event: "command", func, args }),
      "*"
    );
  }

  renderIcons() {
    const html = this.state.muted
      ? `<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
           <path d="M11 5L6.5 9H3v6h3.5L11 19V5Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>
           <path d="M16 9l5 6M21 9l-5 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
         </svg>`
      : `<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
           <path d="M11 5L6.5 9H3v6h3.5L11 19V5Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>
           <path d="M15.5 8.5c1.6 1.6 1.6 5.4 0 7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
           <path d="M17.8 6.2c3 3 3 8.6 0 11.6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
         </svg>`;

    this.iconHosts.forEach((host) => {
      host.innerHTML = html;
    });
  }
}

if (!customElements.get("s-sticky-video")) {
  customElements.define("s-sticky-video", SStickyVideo);
}
